import { DatePicker } from "@mui/lab";
import {
  AlertColor,
  Autocomplete,
  Button,
  CircularProgress,
  MenuItem,
  Modal,
  TextField,
  Theme,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios from "axios";
import React, { useImperativeHandle, useMemo } from "react";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import { Statement, StatementRecord } from "./StatementAccessories";
import { DropDownComboOption } from "../../../component/GenericFilter";
import { SnackBarMessage, SnackBarMessageProps } from "../../../component/SnackBarMessage";
import { GeneralDialog } from "../../../component/GeneralDialog";
import Title from "../../../component/Title";
import moment from "moment";
import { CommonProps } from "../../../component/MainContents";
import { ValidateNumber } from "../../../../common/CommonFunctions";
import { renewToken } from "../../../../services/AuthService";
import { NumberTypes } from "../../../../models/enums/NumberType";
import NumberFormat from "react-number-format";
import { OpenMode } from "../../../../common/Enums";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
  })
);

export interface StatementRecordSingleHandle {
  Initialise: (mode: OpenMode, statementRecord: StatementRecord) => void;
}

export function GetStatementRegion(statementNo: string) {
  return statementNo.substring(2, 4);
}

export const StatementRecordSingle = React.forwardRef(
  (props: CommonProps, ref: React.Ref<StatementRecordSingleHandle>) => {
    const style = { ...useStyles(), ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [hasError, setHasError] = React.useState(false);

    const [selectedCurrency, setSelectedCurrency] = React.useState<string | null>("JPY");

    const [open, setOpen] = React.useState(false);
    const [openMode, setOpenMode] = React.useState<OpenMode>(OpenMode.NEW);
    const [model, setModel] = React.useState<StatementRecord>();
    const [filteredCustomers, setFilteredCustomers] = React.useState<DropDownComboOption[]>([]);

    const [amountErrorText, setAmountErrorText] = React.useState<string>("");

    const [saved, setSaved] = React.useState<boolean>(false);

    //Dialogs and snacks
    const hasChanges = React.useRef<boolean>(false);
    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }


    useImperativeHandle(ref, () => ({
      Initialise(mode: OpenMode, statementRecord: StatementRecord) {
        setOpen(true);
        setSaved(false);
        renewToken();

        hasChanges.current = false;
        setOpenMode(mode);

        FilterCustomersByLanguage([0]); //Always show customer names of any language
        
        let temp: any = { ...model };
        temp = statementRecord;
        if (mode === OpenMode.EDIT)
        {
          temp.customerID = statementRecord.customerID;
          temp.custLangID = statementRecord.custLangID;
        } 
        setSelectedCurrency(temp.currency);
        setModel(temp);
      },
    }));

    function closeModal() {
      setDialogOpen(false);
      setOpen(false);
      props.onClose(false, model!);
    }

    function closeModalCheck(keepOpen: boolean) {
      if (hasChanges.current) {
        setDialogOpen(true);
      } else {
        setDialogOpen(false);
        setOpen(keepOpen);
        props.onClose(saved, model!);
      }
    }

    function isSaveValid(showWarning: boolean) {
      let proceed = true;
      let warning = "";
      let noOfLines = 0;
      if (model) {
        if (model.invoiceNo === "") {
          warning += "Please enter an invoice no.\r\n"
          noOfLines++;
          proceed = false
        }
        if (model.poNo === "") {
          warning += "Please enter a PO no.\r\n"
          noOfLines++;
          proceed = false
        }
        let msg = ValidateNumber(model.amount.toString(), NumberTypes.FLOAT , false);
        if (msg != "") {
          warning += msg;
          noOfLines++;
          proceed = false
        }

        if (!proceed && showWarning) {
          showSnackBar(warning, "", "warning");
        }
      }

      return proceed;
    }

    function Update(
      propertyName: string,
      propertyValue: any,
      validationRegex?: RegExp
    ) {
      let proceed = true;

      if (validationRegex !== undefined) {
        proceed = validationRegex.test(propertyValue);
      }

      if (proceed) {
        let temp: any = { ...model };

        if (temp[propertyName] != propertyValue)
        {
          hasChanges.current = true;
          temp[propertyName] = propertyValue ?? null;
          setModel(temp);
        }
      }
    }

    function CustomerChanged(newCustomer: any) {
      if (newCustomer) {
        let custId = newCustomer.value % 100000;
        let custLangId = Math.trunc(newCustomer.value / 100000);
        let temp: any = { ...model };
        temp.customerID = custId;
        temp.custLangID = custLangId;
        setModel(temp);
      }
    }

    function FilterCustomersByLanguage(langIds: number[]) {
      let temp: DropDownComboOption[] = props.customers;
      let result: DropDownComboOption[] = [];
      if (langIds.findIndex( x => x === 0) > -1)
      {
        setFilteredCustomers(props.customers);
      } 
      else
      {
        langIds.forEach((id) => {
          result = result.concat(Object.values(temp).filter(c => Math.trunc(parseInt(c.value) / 100000) === id));
        });
        setFilteredCustomers(result);
      }
    }

    function SaveChanges(
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      close: boolean
    ) {
      if (isSaveValid(true)) {
        event.preventDefault();
        event.stopPropagation();
        let m = model;
        if (m)
        {
          m.invoiceDate = moment(m.invoiceDate).format();
          setModel(m);
        }

        if (openMode === OpenMode.EDIT)
        {
          axios.put<Statement>('Api/V1/Statement/StatementRecord', model).then(
            (result) => {
              let temp: any = { ...model };
              temp = result.data;
              temp.staff = ""; //staff cannot be null when save
              setModel(temp);
  
              showSnackBar("Statement record saved successfully.", "", "success");
              hasChanges.current = false;
              setOpen(!close);
              setDialogOpen(false);
            },
            (error) => {
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
          );
        } 
        else
        {
          axios.post<Statement>('Api/V1/Statement/StatementRecord', model).then(
            (result) => {
              let temp: any = { ...model };
              temp = result.data;
              temp.staff = ""; //staff cannot be null when save
              setModel(temp);
  
              showSnackBar("Statement record created successfully.", "", "success");
              hasChanges.current = false;
              setSaved(true);
              setOpenMode(OpenMode.EDIT);
              setOpen(!close);
              setDialogOpen(false);
            },
            (error) => {
              showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
            }
          );
        }
      }
    }

    return (
      <div>
        <Modal open={open} onClose={() => closeModalCheck(true)}>
          <div className={style.ModalMainNarrow}>
            {model ? (
              <div className={style.ModalBody}>
                <Title pageTitle="Statement Record" />
                <div className={style.ModalContent}>
                  <div className={style.ModalFields}>
                    <Autocomplete className={style.ModalContentFieldFullWidth}
                      limitTags={2}
                      onChange={(e, selected) => CustomerChanged(selected)}
                      options={filteredCustomers}
                      disabled={true}
                      getOptionLabel={(option: DropDownComboOption) => option.name}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.value}>
                            {option.name}
                          </li>
                        );
                      }}
                      value={filteredCustomers.find(s => s.value === (model.customerID + model.custLangID * 100000).toString()) || null}
                      style={{ minWidth: tinyScreen ? "274px" : (smallScreen ? "374px" : "494px") }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={"Customer"}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </div>
                  <div className={style.ModalFields}>
                    <div className={style.ModalContentFieldTwoTenthWidthLeft}
                      style={{ minWidth: tinyScreen ? "160px" : (smallScreen ? "200px" : "240px") }}
                    >
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          key="date"
                          label="Date"
                          inputFormat="yyyy/MM/dd"
                          mask="____/__/__"
                          value={Date.parse(model.invoiceDate)}
                          onChange={(newValue) => Update("invoiceDate", moment(newValue).format('YYYY/MM/DD'))}
                          renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} size="small" />}
                        />
                      </LocalizationProvider>
                    </div>
                    <TextField className={style.ModalContentFieldOneTenthWidthRight}
                      value={model.region}
                      label="Region"
                      onChange={(e) => Update("region", e.target.value)}
                      select
                      size="small"
                      fullWidth
                    >
                      {props.regions.map((item)=> (
                          <MenuItem key={item.name + "-" + item.value} value={item.name}>
                              {item.name}
                          </MenuItem>
                      ))}
                  </TextField>
                  </div>
                  <div className={style.ModalFields}>
                    <TextField className={style.ModalContentFieldFullWidth}
                      value={model.invoiceNo}
                      label="Invoice No."
                      onChange={(e) => Update("invoiceNo", e.target.value.slice(0,15))}
                      size="small"
                      fullWidth
                    />
                  </div>
                  <div className={style.ModalFields}>
                    <TextField className={style.ModalContentFieldFullWidth}
                      value={model.poNo}
                      label="PO No."
                      onChange={(e) => Update("poNo", e.target.value.slice(0,150))}
                      size="small"
                      fullWidth
                    />
                  </div>
                  <div className={style.ModalFields}>
                    <TextField className={style.ModalContentFieldOneTenthWidthLeft}
                      label="Purchase Currency"
                      value={selectedCurrency}
                      onChange={(e) => {
                        setSelectedCurrency(e.target.value);
                        Update("currency", e.target.value);
                      }}
                      style={{ minWidth: tinyScreen ? "80px" : (smallScreen ? "100px" : "120px") }}
                      select
                      size="small"
                    >
                      {props.currencies.map((item) => (
                        <MenuItem key={item.name + "-" + item.value} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>                                        
                    <NumberFormat className={`${style.ModalContentFieldTwoTenthWidthRight}`}
                      customInput={TextField}
                      value={model.amount}
                      label="Amount"
                      onValueChange={(e) => {
                        if (e.value !== "") {
                          let msg = ValidateNumber(e.value, NumberTypes.FLOAT, false);
                          setAmountErrorText(msg);
                          if (msg === "")
                            Update("amount", e.value);
                        }
                      }}                      
                      error={amountErrorText !== ""}
                      helperText={amountErrorText}
                      decimalScale={selectedCurrency=='JPY' ? 0 : 2}
                      thousandSeparator
                      size="small"
                      fullWidth
                      />
                </div>
              </div>

              <div className={style.ModalFooter}>
                <Button
                  variant="contained"
                  color="secondary"
                  className={style.margin}
                  onClick={(e) => SaveChanges(e, false)}
                  size="small"
                  style={{ padding: "1px" }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  className={style.margin}
                  onClick={(e) => closeModalCheck(false)}
                  size="small"
                  style={{ padding: "1px" }}
                >
                  Close
                </Button>
              </div>

              </div>
            ) : (
              <div style={{ textAlign: "center" }}>
                <CircularProgress color="primary" />
              </div>
            )}
          </div>
        </Modal>

        <SnackBarMessage {...snackSettings} />

        <GeneralDialog
          title={"Save Changes?"}
          message={
            "Changes have been made, did you want to save before closing?"
          }
          open={dialogOpen}
          options={[
            {
              text: "Save",
              action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => SaveChanges(event, true),
              color: "secondary",
              variant: "contained",
            },
            {
              text: "No",
              action: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => closeModal(),
              color: "primary",
              variant: "contained",
            },
          ]}
        />

      </div>
    );


  }

);
